import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Plus, Search, X, Download, FileText, FolderOpen } from 'lucide-react';

const BODocuments = () => {
  const { role } = useBOAuth();
  const [collections, setCollections] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [filterEstate, setFilterEstate] = useState('');
  const [form, setForm] = useState({ client_id: '', collected_by: '', collector_phone: '', collector_address: '', collection_date: new Date().toISOString().split('T')[0], notes: '' });
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const isReadOnly = role === 'accountant';

  const fetchCollections = async () => {
    try {
      const params = {};
      if (filterEstate) params.estate = filterEstate;
      if (search) params.search = search;
      const res = await boAPI.getDocumentCollections(params);
      setCollections(res.data.collections || []);
    } catch (e) { toast.error('Failed to load collections'); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const res = await boAPI.getClients();
      setClients((res.data.clients || []).filter(c => c.assigned_plot_id));
    } catch (e) { console.error('fetchClients (documents) failed:', e); }
  };

  useEffect(() => { fetchCollections(); fetchClients(); }, []);
  useEffect(() => { fetchCollections(); }, [filterEstate]);

  const handleSearch = (e) => { e.preventDefault(); fetchCollections(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.collected_by.trim()) { toast.error('Client and collector name required'); return; }
    setSubmitting(true);
    try {
      await boAPI.recordDocumentCollection(form);
      toast.success('Document collection recorded');
      setShowForm(false);
      setForm({ client_id: '', collected_by: '', collector_phone: '', collector_address: '', collection_date: new Date().toISOString().split('T')[0], notes: '' });
      fetchCollections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record'); }
    finally { setSubmitting(false); }
  };

  const handleExport = async () => {
    try {
      const res = await boAPI.exportDocumentCollectionsCSV();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `medichi_document_collections_${new Date().toISOString().split('T')[0]}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const selectedClient = clients.find(c => c.id === form.client_id);
  const estateNames = [...new Set(collections.map(c => c.estate_name).filter(Boolean))].sort();

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-documents-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Document Collection</h1>
            <p className="text-neutral-500 mt-1">{collections.length} record{collections.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="rounded-xl" data-testid="export-docs-btn"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            {!isReadOnly && (
              <Button onClick={() => setShowForm(true)} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="record-collection-btn"><Plus className="h-4 w-4 mr-2" /> Record Collection</Button>
            )}
          </div>
        </div>

        {/* Record Collection Form */}
        {showForm && (
          <Card className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Record Document Collection</CardTitle><CardDescription>Enter details of who collected the documents</CardDescription></div>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 relative">
                  <Label>Client *</Label>
                  <Input
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); if (!e.target.value) setForm({ ...form, client_id: '' }); }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="Type client name to search..."
                    className="mt-1.5 rounded-xl"
                    data-testid="doc-client-search"
                  />
                  {showClientDropdown && clientSearch && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {clients.filter(c => c.full_name?.toLowerCase().includes(clientSearch.toLowerCase())).slice(0, 10).map(c => (
                        <button key={c.id} type="button" onClick={() => { setForm({ ...form, client_id: c.id }); setClientSearch(c.full_name); setShowClientDropdown(false); }} className="w-full text-left px-3 py-2 hover:bg-primary-50 text-sm border-b last:border-b-0" data-testid={`client-option-${c.id}`}>
                          <p className="font-medium">{c.full_name}</p>
                          <p className="text-xs text-neutral-500">{c.plot_info?.plot_number || ''} - {c.plot_info?.estate_name || ''}</p>
                        </button>
                      ))}
                      {clients.filter(c => c.full_name?.toLowerCase().includes(clientSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-sm text-neutral-500">No clients found</p>
                      )}
                    </div>
                  )}
                  {selectedClient && (
                    <div className="mt-2 p-3 bg-neutral-50 rounded-xl text-xs space-y-1">
                      <p><span className="text-neutral-500">Estate:</span> <span className="font-medium">{selectedClient.plot_info?.estate_name}</span></p>
                      <p><span className="text-neutral-500">Plot:</span> <span className="font-medium">{selectedClient.plot_info?.plot_number}</span></p>
                      <p><span className="text-neutral-500">Block:</span> <span className="font-medium">{selectedClient.plot_info?.location_block || '--'}</span></p>
                    </div>
                  )}
                </div>
                <div><Label>Collected By (Name) *</Label><Input value={form.collected_by} onChange={(e) => setForm({ ...form, collected_by: e.target.value })} placeholder="Person who picked up documents" className="mt-1.5 rounded-xl" data-testid="doc-collected-by" /></div>
                <div><Label>Collector's Phone</Label><Input value={form.collector_phone} onChange={(e) => setForm({ ...form, collector_phone: e.target.value })} placeholder="Phone number" className="mt-1.5 rounded-xl" /></div>
                <div><Label>Collector's Address</Label><Input value={form.collector_address} onChange={(e) => setForm({ ...form, collector_address: e.target.value })} placeholder="Address" className="mt-1.5 rounded-xl" /></div>
                <div><Label>Date of Collection *</Label><Input type="date" value={form.collection_date} onChange={(e) => setForm({ ...form, collection_date: e.target.value })} className="mt-1.5 rounded-xl" data-testid="doc-date" /></div>
                <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes" className="mt-1.5 rounded-xl" /></div>
                <div className="sm:col-span-2 flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="doc-submit">{submitting ? 'Recording...' : 'Record Collection'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select value={filterEstate} onChange={(e) => setFilterEstate(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
            <option value="">All Estates</option>
            {estateNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <form onSubmit={handleSearch} className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client or collector..." className="pl-10 rounded-xl text-sm" />
          </form>
        </div>

        {/* Collections Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm" data-testid="documents-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-3 py-3 font-medium text-neutral-600">Date</th>
                  <th className="text-left px-3 py-3 font-medium text-neutral-600">Client</th>
                  <th className="text-left px-3 py-3 font-medium text-neutral-600 hidden sm:table-cell">Estate</th>
                  <th className="text-left px-3 py-3 font-medium text-neutral-600 hidden md:table-cell">Plot / Block</th>
                  <th className="text-left px-3 py-3 font-medium text-neutral-600">Collected By</th>
                  <th className="text-left px-3 py-3 font-medium text-neutral-600 hidden lg:table-cell">Collector Phone</th>
                  <th className="text-left px-3 py-3 font-medium text-neutral-600 hidden lg:table-cell">Notes</th>
                </tr>
              </thead>
              <tbody>
                {collections.map((d) => (
                  <tr key={d.id} className="border-b hover:bg-neutral-50 transition-colors">
                    <td className="px-3 py-3 whitespace-nowrap">{d.collection_date}</td>
                    <td className="px-3 py-3"><p className="font-medium">{d.client_name}</p><p className="text-xs text-neutral-400">{d.client_phone}</p></td>
                    <td className="px-3 py-3 hidden sm:table-cell text-neutral-600">{d.estate_name || '--'}</td>
                    <td className="px-3 py-3 hidden md:table-cell text-neutral-600">{d.plot_number} {d.block_number ? `(${d.block_number})` : ''}</td>
                    <td className="px-3 py-3 font-medium">{d.collected_by}</td>
                    <td className="px-3 py-3 hidden lg:table-cell text-neutral-500">{d.collector_phone || '--'}</td>
                    <td className="px-3 py-3 hidden lg:table-cell text-neutral-500 max-w-[150px] truncate">{d.notes || '--'}</td>
                  </tr>
                ))}
                {collections.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-neutral-500"><FolderOpen className="h-8 w-8 mx-auto mb-2 text-neutral-300" />No document collections recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </BOLayout>
  );
};

export default BODocuments;
